import { FactoryProvider, Provider, TransferState, inject, makeStateKey } from '@angular/core';

import { ApolloClientOptions, ApolloLink, NormalizedCacheObject } from '@apollo/client/core';
import { APOLLO_OPTIONS, Apollo } from 'apollo-angular';
import { HttpLink } from 'apollo-angular/http';

import { EnvironmentService, PlatformService } from '@hdms/shared/util-core';

import { CustomInMemoryCache } from './cache/custom-in-memory.cache';
import { IN_MEMORY_CACHE_CONFIG } from './cache/in-memory-cache.config';
import { AuthLink } from './links/auth.link';
import { createErrorLink } from './links/error.link';
import { createHeadersLink } from './links/headers.link';
import { createReadyLink } from './links/ready.link';

const STATE_KEY = makeStateKey<NormalizedCacheObject>('apollo.state');
const APOLLO_CLIENT_NAME = 'hdms-frontend';
const APOLLO_DEFAULT_VERSION = 'default';

/**
 * Configures Apollo client options
 * - links
 * - cache
 * - SSR options:
 *   - in browser queries with `forceFetch` enabled will be delayed
 *   - on server avoid to run twice queries with `forceFetch` enabled
 * - name and version - required for Apollo Studio
 * - don't connect to dev tools on server side as this will set a 10 sec timeout blocking app from becoming stable
 */
function setupApolloClientOptions(): ApolloClientOptions<NormalizedCacheObject> {
  const { isBrowser } = inject(PlatformService);
  const envService = inject(EnvironmentService);
  const { isProduction, releaseVersion } = envService;

  const ssrOptions = isBrowser ? { ssrForceFetchDelay: 100 } : { ssrMode: true };

  return {
    cache: setupInMemoryCache(isBrowser),
    link: setupLinkChain(envService),
    ...ssrOptions,
    name: APOLLO_CLIENT_NAME,
    version: isProduction ? releaseVersion : APOLLO_DEFAULT_VERSION,
    connectToDevTools: isBrowser && !isProduction
  };
}

/**
 * Configure Apollo Link chain:
 * - ErrorLink
 * - ReadyLink
 * - AuthLink (standalone)
 * - HeadersLink
 * - HttpLinkHandler
 *
 * For processing the response, order in links array is right to left
 */
function setupLinkChain({ gqlEndpointUri, isStandalone }: EnvironmentService): ApolloLink {
  const httpLinkHandler = inject(HttpLink).create({
    uri: gqlEndpointUri,
    withCredentials: isStandalone
  });

  const links = [
    createErrorLink(),
    createReadyLink(),
    isStandalone && new AuthLink(),
    createHeadersLink(),
    httpLinkHandler
  ].filter(Boolean);

  return ApolloLink.from(links);
}

/**
 * Create a CustomInMemoryCache instance and configure
 * TransferState for serializing and restoring the cache
 */
function setupInMemoryCache(isBrowser: boolean): CustomInMemoryCache {
  const cache = new CustomInMemoryCache(IN_MEMORY_CACHE_CONFIG);
  const transferState = inject(TransferState, { optional: true });

  if (transferState) {
    if (isBrowser) {
      const state = transferState.get<NormalizedCacheObject>(STATE_KEY, null);
      state && cache.restore(state);
    } else {
      transferState.onSerialize(STATE_KEY, () => cache.extract());
    }
  }

  return cache;
}

const APOLLO_OPTIONS_PROVIDER: FactoryProvider = {
  provide: APOLLO_OPTIONS,
  useFactory: setupApolloClientOptions
};

export const GRAPHQL_PROVIDERS: Provider[] = [Apollo, APOLLO_OPTIONS_PROVIDER];
